import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CreditCard, LayoutDashboard, Layers } from 'lucide-react';
import { INTENT_OPTIONS, type IntentValue } from '../post-signup-onboarding-constants';
import { cn } from '@/lib/utils';

interface IntentStepProps {
	value: IntentValue | '';
	onChange: (value: IntentValue) => void;
	onNext: () => void;
}

const INTENT_ICONS: Record<IntentValue, React.ReactNode> = {
	PROCESSING: <CreditCard className="h-5 w-5" />,
	CRM: <LayoutDashboard className="h-5 w-5" />,
	BOTH: <Layers className="h-5 w-5" />,
};

/** Step 1: What are you looking for? (PROCESSING / CRM / BOTH). */
export function IntentStep({ value, onChange, onNext }: IntentStepProps) {
	const { t } = useTranslation('onboarding');
	return (
		<div className="space-y-8">
			<div className="space-y-2">
				<h2 className="text-foreground text-2xl font-bold tracking-tight">
					{t('intent.title')}
				</h2>
				<p className="text-muted-foreground text-sm">
					{t('intent.subtitle')}
				</p>
			</div>
			<RadioGroup
				value={value}
				onValueChange={(v) => onChange(v as IntentValue)}
				className="mx-auto grid max-w-xl gap-3"
			>
				{INTENT_OPTIONS.map((opt) => {
					const isSelected = value === opt.value;
					return (
						<div
							key={opt.value}
							className={cn(
								'flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 transition-all',
								isSelected
									? 'border-primary bg-primary/5 shadow-sm'
									: 'border-border hover:border-primary/40 hover:bg-accent/50',
							)}
							onClick={() => onChange(opt.value)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === 'Enter' && onChange(opt.value)}
						>
							<div
								className={cn(
									'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
									isSelected
										? 'bg-primary text-primary-foreground'
										: 'bg-primary/10 text-primary',
								)}
							>
								{INTENT_ICONS[opt.value]}
							</div>
							<Label
								htmlFor={opt.value}
								className="cursor-pointer flex-1 text-sm font-medium"
							>
								{t(`intent.${opt.value}`)}
							</Label>
							<RadioGroupItem
								value={opt.value}
								id={opt.value}
								className="flex-shrink-0"
							/>
						</div>
					);
				})}
			</RadioGroup>
			<Button
				onClick={onNext}
				disabled={!value}
				size="lg"
				className="w-full max-w-md"
			>
				{t('intent.next')}
			</Button>
		</div>
	);
}
